import cx from 'classnames'
import ColorChangeButton from './ColorChangeButton/ColorChangeButton'
import Styles from './EditorColorPicker.module.scss'
import { MouseEventHandler, PropsWithChildren } from 'react'

type basicProps = {
  'aria-label'?: (color: string) => string
  activeColors: string[]
  colorOptions: string[]
  defaultColor?: undefined
  includeDropdownStyles?: boolean
  noColorButtonPos?: undefined
  noColorButtonLabel?: undefined
  removeColor?: undefined
  setColor: (color: string) => void
  stopPropagation?: boolean
}

type noColorProps = Omit<basicProps, 'noColorButtonPos' | 'noColorButtonLabel' | 'defaultColor' | 'removeColor'> & {
  noColorButtonPos: 'top' | 'bottom'
  noColorButtonLabel: string
  defaultColor: string
  removeColor: () => void
}

type Props = basicProps | noColorProps

export const rgbToHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')

export const colorToHex = (color: string) => {
  if (/^rgb\(/.test(color)) {
    const parts = color.split(',')
    const rgb = {
      r: parseInt(parts[0].substring(4)),
      g: parseInt(parts[1]),
      b: parseInt(parts[2]),
    }
    return rgbToHex(rgb.r, rgb.g, rgb.b).toUpperCase()
  } else return color
}

const NoColorButton = ({ onClick, children }: PropsWithChildren<{ onClick: MouseEventHandler }>) => (
  <button className={Styles.noColorButton} data-testid="remove-color-button" onClick={onClick}>
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10" cy="10" r="9.5" fill="white" stroke="#DFE1E6" />
      <path d="M17 3L3 17" stroke="#FFBDB8" />
    </svg>
    {children}
  </button>
)

export const EditorColorPicker = ({
  'aria-label': ariaLabel,
  activeColors,
  colorOptions,
  defaultColor,
  includeDropdownStyles = true,
  noColorButtonPos,
  noColorButtonLabel,
  removeColor,
  setColor,
  stopPropagation = true,
}: Props) => {
  const convertActiveColorsToHex = (): string[] =>
    activeColors.map((color) => {
      return colorToHex(color)
    })

  return (
    <div className={cx({ [Styles.dropdownContainer]: includeDropdownStyles })}>
      {noColorButtonPos && noColorButtonPos === 'top' && (
        <div className={Styles.noColorButtonTop}>
          <NoColorButton
            onClick={(e) => {
              if (stopPropagation) e.stopPropagation()
              removeColor?.()
            }}
          >
            {noColorButtonLabel}
          </NoColorButton>
        </div>
      )}
      <div className={Styles.colorOptions}>
        {colorOptions.map((color) => (
          <ColorChangeButton
            aria-label={ariaLabel}
            key={color}
            color={color}
            active={convertActiveColorsToHex().includes(color)}
            data-testid={`color-button-${color}`}
            onClick={() => setColor(color)}
            stopPropagation={stopPropagation}
          />
        ))}
      </div>
      {noColorButtonPos && noColorButtonPos === 'bottom' && (
        <div className={Styles.noColorButtonBottom}>
          <NoColorButton
            onClick={(e) => {
              if (stopPropagation) e.stopPropagation()
              removeColor?.()
            }}
          >
            {noColorButtonLabel}
          </NoColorButton>
        </div>
      )}
    </div>
  )
}
